import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { fetchSampleById } from "../../../utils/api";
import FocusedFlowPage from "../../../components/FocusedFlowPage";
import { BIOBANK_ROUTES, BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const AliquotVerify = () => {
  const { barcode } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const encounterId = state?.encounterId ?? null;
  const returnTo = state?.returnTo || BIOBANK_ROUTES.SAMPLE_PROCESSING;

  const [sample, setSample] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const s = await fetchSampleById(barcode);
        if (!mounted) return;
        setSample(
          s
            ? {
                barcode: s.sampleId || "N/A",
                type: s.sampleType || "N/A",
                quality: s.sampleQuality || "N/A",
              }
            : null
        );
      } catch (e) {
        console.error("Failed to fetch samples", e);
        if (mounted) setSample(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [barcode]);

  const beginProcessing = () => {
    if (!sample) return;
    const ctx = {
      encounterId,
      returnTo,
      sampleMeta: sample,
      sampleId: sample.barcode,
    };
    sessionStorage.setItem("aliquotFlow", JSON.stringify(ctx));
    navigate(BIOBANK_SUB_ROUTES.SAMPLE_PROCESSING_PROCESS(encodeURIComponent(sample.barcode)), {
      state: ctx,
    });
  };

  if (loading) {
    return (
      <FocusedFlowPage title="Verify Sample for Sample Processing" maxWidth={640}>
        <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      </FocusedFlowPage>
    );
  }

  if (!sample) {
    return (
      <FocusedFlowPage
        title="Sample not found"
        maxWidth={640}
        onClose={() => navigate(returnTo)}
        actions={[
          <Button key="back" variant="contained" onClick={() => navigate(returnTo)}>
            Go Back
          </Button>,
        ]}
      >
        <Box>
          <Typography sx={{ mt: 1 }}>
            We couldn’t find a sample with Barcode ID <strong>{barcode}</strong>
            .
          </Typography>
        </Box>
      </FocusedFlowPage>
    );
  }

  return (
    <FocusedFlowPage
      title="Verify Sample for Sample Processing"
      subtitle="Please review the following information to verify you’ve selected the correct BioSpecimen Sample for Sample Processing."
      onClose={() => navigate(returnTo)}
      maxWidth={640}
      actions={[
        <Button
          key="back"
          variant="outlined"
          onClick={() => navigate(returnTo)}
          startIcon={<ArrowBackIcon />}
          sx={{
            borderColor: "#8C5A3C",
            color: "#8C5A3C",
            fontWeight: 700,
            textTransform: "none",
            px: 3.5,
            height: 40,
            "&:hover": { borderColor: "#8C5A3C", background: "#FBF6F3" },
          }}
        >
          Back
        </Button>,
        <Button
          key="begin"
          variant="contained"
          onClick={beginProcessing}
          endIcon={<ArrowForwardIcon />}
          sx={{
            bgcolor: "#500000",
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            px: 3.5,
            height: 40,
            "&:hover": { bgcolor: "#6a0000" },
          }}
        >
          Begin Processing
        </Button>,
      ]}
    >
      <Box sx={{ display: "grid", gap: 2 }}>
        <Typography sx={{ fontSize: 15, color: "#2E2E2E" }}>
          <strong>Barcode ID:</strong>&nbsp;{sample.barcode}
        </Typography>
        <Typography sx={{ fontSize: 15, color: "#2E2E2E" }}>
          <strong>Sample Type:</strong>&nbsp;{sample.type}
        </Typography>
        <Typography sx={{ fontSize: 15, color: "#2E2E2E" }}>
          <strong>Sample Quality:</strong>&nbsp;{sample.quality}
        </Typography>
      </Box>
    </FocusedFlowPage>
  );
};

export default AliquotVerify;
